import { HTMLAttributes, useId, useState } from 'react';

import { twMerge } from 'tailwind-merge';
import { AnimatePresence, motion } from 'motion/react';
import { ActionButton, Typography } from '../../../ui';
import { IconAngleUp } from '@maia/icons';
import { match } from 'ts-pattern';
import { BillingUnitDto } from '@maia/shared';

type Props = {
  totalPremium: string;
  insuranceTax: string;
  items?: Array<{
    title: string;
    price: string;
  }>;
  billingUnit: BillingUnitDto;
} & Omit<HTMLAttributes<HTMLDivElement>, 'children'>;

export function TotalsCard(props: Props) {
  const id = useId();
  const [isExtended, setIsExtended] = useState<boolean>(false);

  const { totalPremium, insuranceTax, items, billingUnit, className, ...rest } =
    props;

  return (
    <div
      className={twMerge(
        'rounded-12 border-grayscale100 overflow-hidden border transition-all',
        !isExtended && 'border-hidden',
        className,
      )}
      {...rest}
    >
      <AnimatePresence>
        {items && items.length > 1 && (
          <motion.div
            initial={{ height: 0 }}
            exit={{ height: 0 }}
            animate={isExtended ? { height: 'auto' } : { height: 0 }}
          >
            <div className="space-y-8 p-16 bg-grayscale0 flex flex-col">
              {items.map((item, key) => (
                <div
                  className="flex justify-between pr-32"
                  key={`totals-card-${id}-item-${key}`}
                >
                  <Typography
                    variant="body-xxs"
                    weight="regular"
                    className="text-grayscale400"
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body-xxs"
                    weight="semibold"
                    className="text-grayscale400"
                  >
                    {item.price}
                  </Typography>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="space-x flex items-center space-x-8 p-16 bg-primary50 z-10 relative">
        <div className="flex flex-row items-center flex-1">
          <div className="flex flex-col flex-1">
            <Typography
              variant="body-xs"
              weight="medium"
              className="text-grayscale600"
            >
              Totaalpremie {billingUnit.label}
            </Typography>
            <Typography
              variant="body-xs"
              weight="regular"
              className="text-grayscale400"
            >
              Incl. assurantiebelasting van {insuranceTax}
            </Typography>
          </div>
          <Typography
            variant="body-s"
            weight="semibold"
            className="text-primaryMain"
          >
            {totalPremium}
          </Typography>
        </div>

        {items && items?.length > 0 && (
          <ActionButton
            icon={IconAngleUp}
            className={twMerge(
              'rotate-0 transition-transform',
              isExtended && 'rotate-180',
            )}
            size="small"
            onPress={() => setIsExtended((_) => !_)}
          />
        )}
      </div>
    </div>
  );
}
